/**
 * ToR Criteria Page
 * 
 * Upload a ToR document to extract key submission criteria:
 * - Deadline dates
 * - Date for clarification
 * - Type of submission
 * - Submission method
 * - Proposal requirements
 * - Award / evaluation criteria
 * - Eligibility criteria
 * - Financial parameters
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Alert,
    CircularProgress,
    IconButton,
    Tooltip,
    Snackbar
} from '@mui/material';
import {
    UploadFile as UploadFileIcon,
    ContentCopy as ContentCopyIcon,
    Refresh as RefreshIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import axios from 'axios';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

const API_URL = process.env.REACT_APP_API_URL || '/api';

interface TorCriteria {
    deadline_dates: string | object[] | object;
    deadline_source?: string | null;
    clarification_date: string | object[] | object;
    submission_type: string | object[] | object;
    submission_method: string | object[] | object;
    submission_source?: string | null;
    proposal_requirements: string | object[] | object;
    award_criteria: string | object[] | object;
    eligibility_criteria: string | object[] | object;
    eligibility_source?: string | null;
    financial_parameters: string | object[] | object;
    financial_source?: string | null;
    project_title?: string;
}

// Helper function to format key names nicely
const formatKey = (key: string): string => {
    return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// Color scheme for eligibility categories
const ELIGIBILITY_COLORS = {
    mandatory: '#ef5350',      // Red - must have
    expectations: '#ff9800',   // Orange/Amber - high expectation
    preferences: '#22b8a7'     // Teal - nice to have
};

// Source citation component - displays where info was extracted from
const SourceCitation: React.FC<{ source: string | null | undefined }> = ({ source }) => {
    if (!source) return null;
    return (
        <Typography 
            component="div" 
            sx={{ 
                fontStyle: 'italic', 
                color: 'rgba(255,255,255,0.5)', 
                fontSize: '0.75rem',
                mt: 0.5,
                pl: 1,
                borderLeft: '2px solid rgba(255,255,255,0.2)'
            }}
        >
            {source}
        </Typography>
    );
};

// Render eligibility criteria with color-coded sections
const renderEligibilityCriteria = (value: string | object[] | object | unknown): React.ReactNode => {
    if (!value) return <span style={{ color: '#ff9800', fontStyle: 'italic' }}>Not found</span>;
    
    if (typeof value === 'string') {
        if (value.toLowerCase() === 'not found') {
            return <span style={{ color: '#ff9800', fontStyle: 'italic' }}>Not found</span>;
        }
        
        // Parse the string to extract MANDATORY, EXPECTATIONS, PREFERENCES sections
        const sections: { type: string; items: string[]; color: string }[] = [];
        
        // Split by section headers
        const mandatoryMatch = value.match(/MANDATORY:?\s*([\s\S]*?)(?=EXPECTATIONS:|PREFERENCES:|$)/i);
        const expectationsMatch = value.match(/EXPECTATIONS:?\s*([\s\S]*?)(?=MANDATORY:|PREFERENCES:|$)/i);
        const preferencesMatch = value.match(/PREFERENCES:?\s*([\s\S]*?)(?=MANDATORY:|EXPECTATIONS:|$)/i);
        
        // Helper to parse items - split on newlines followed by bullet/dash, not mid-text hyphens
        const parseItems = (text: string): string[] => {
            // First try splitting by newline + bullet pattern
            const byNewline = text.split(/\n\s*[-•]\s*/).filter(item => item.trim());
            if (byNewline.length > 1) {
                return byNewline;
            }
            // If no newlines, try splitting by " - " (space-dash-space) which indicates list separator
            const bySpaceDash = text.split(/\s+-\s+/).filter(item => item.trim());
            if (bySpaceDash.length > 1) {
                return bySpaceDash;
            }
            // Fallback: return as single item if no clear separators
            return text.trim() ? [text.trim()] : [];
        };
        
        if (mandatoryMatch && mandatoryMatch[1].trim()) {
            const items = parseItems(mandatoryMatch[1]);
            if (items.length > 0) {
                sections.push({ type: 'MANDATORY', items, color: ELIGIBILITY_COLORS.mandatory });
            }
        }
        
        if (expectationsMatch && expectationsMatch[1].trim()) {
            const items = parseItems(expectationsMatch[1]);
            if (items.length > 0) {
                sections.push({ type: 'EXPECTATIONS', items, color: ELIGIBILITY_COLORS.expectations });
            }
        }
        
        if (preferencesMatch && preferencesMatch[1].trim()) {
            const items = parseItems(preferencesMatch[1]);
            if (items.length > 0) {
                sections.push({ type: 'PREFERENCES', items, color: ELIGIBILITY_COLORS.preferences });
            }
        }
        
        // If no sections found, just return the raw value
        if (sections.length === 0) {
            return <div style={{ whiteSpace: 'pre-wrap' }}>{value}</div>;
        }
        
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {sections.map((section, idx) => (
                    <Box 
                        key={idx}
                        sx={{ 
                            borderLeft: `3px solid ${section.color}`,
                            pl: 2,
                            py: 1
                        }}
                    >
                        <Typography 
                            component="div" 
                            sx={{ 
                                fontWeight: 700, 
                                color: section.color,
                                fontSize: '0.8rem',
                                letterSpacing: '0.5px',
                                mb: 1
                            }}
                        >
                            {section.type}
                        </Typography>
                        {section.items.map((item, itemIdx) => (
                            <div key={itemIdx} style={{ marginBottom: '4px' }}>
                                • {item.trim()}
                            </div>
                        ))}
                    </Box>
                ))}
            </Box>
        );
    }
    
    // Fallback for non-string values
    return renderFormattedValue(value);
};

// Render value with styled labels - returns React elements
const renderFormattedValue = (value: string | object[] | object | unknown): React.ReactNode => {
    if (!value) return <span style={{ color: '#ff9800', fontStyle: 'italic' }}>Not found</span>;
    
    if (typeof value === 'string') {
        if (value.toLowerCase() === 'not found') {
            return <span style={{ color: '#ff9800', fontStyle: 'italic' }}>Not found</span>;
        }
        return <div style={{ whiteSpace: 'pre-wrap' }}>{value}</div>;
    }
    
    if (Array.isArray(value)) {
        return (value as unknown[]).map((item: unknown, idx: number) => {
            if (typeof item === 'string') {
                return <div key={idx}>• {item}</div>;
            }
            if (typeof item === 'object' && item !== null) {
                const entries = Object.entries(item)
                    .filter(([_, val]) => val && val !== 'Not found' && val !== 'Not applicable');
                return (
                    <div key={idx}>
                        • {entries.map(([_, val]) => String(val)).join(' - ')}
                    </div>
                );
            }
            return <div key={idx}>• {String(item)}</div>;
        });
    }
    
    if (typeof value === 'object' && value !== null) {
        const entries = Object.entries(value as Record<string, unknown>)
            .filter(([_, val]) => val && val !== 'Not found' && val !== 'Not applicable');
        return entries.map(([key, val], idx) => (
            <div key={idx}>
                <span style={{ color: '#22b8a7', fontWeight: 600 }}>{formatKey(key)}:</span> {String(val)}
            </div>
        ));
    }
    
    return String(value);
};

// Plain text version for clipboard copy
const formatValueForCopy = (value: string | object[] | object | unknown): string => {
    if (!value) return 'Not found';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
        return (value as unknown[]).map((item: unknown) => {
            if (typeof item === 'string') return `• ${item}`;
            if (typeof item === 'object' && item !== null) {
                const entries = Object.entries(item)
                    .filter(([_, val]) => val && val !== 'Not found' && val !== 'Not applicable')
                    .map(([_, val]) => `${val}`)
                    .join(' - ');
                return `• ${entries}`;
            }
            return `• ${String(item)}`;
        }).join('\n');
    }
    if (typeof value === 'object' && value !== null) {
        return Object.entries(value as Record<string, unknown>)
            .filter(([_, val]) => val && val !== 'Not found' && val !== 'Not applicable')
            .map(([key, val]) => `${formatKey(key)}: ${val}`)
            .join('\n');
    }
    return String(value);
};

// Check if value represents "not found"
const isValueNotFound = (value: string | object[] | object | unknown): boolean => {
    if (!value) return true;
    if (typeof value === 'string') {
        return value.toLowerCase() === 'not found' || value.trim() === '';
    }
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') {
        const entries = Object.entries(value as Record<string, unknown>)
            .filter(([_, val]) => val && val !== 'Not found' && val !== 'Not applicable');
        return entries.length === 0;
    }
    return false;
};

// Results component - single copyable block
interface CriteriaResultsProps {
    criteria: TorCriteria;
    onReset: () => void;
    onCopySuccess: (message: string) => void;
}

const CriteriaResults: React.FC<CriteriaResultsProps> = ({ criteria, onReset, onCopySuccess }) => {
    // Build formatted text for display and copying
    const formattedText = useMemo(() => {
        const sections: string[] = [];
        
        // Project Title
        if (criteria.project_title) {
            sections.push(`PROJECT: ${criteria.project_title}`);
            sections.push('');
        }
        
        // Deadline Dates
        sections.push('DEADLINE DATES');
        sections.push(isValueNotFound(criteria.deadline_dates) 
            ? 'Not found' 
            : formatValueForCopy(criteria.deadline_dates));
        sections.push('');
        
        // Clarification Date
        sections.push('CLARIFICATION DEADLINE');
        sections.push(isValueNotFound(criteria.clarification_date) 
            ? 'Not found' 
            : formatValueForCopy(criteria.clarification_date));
        sections.push('');
        
        // Submission Type
        sections.push('TYPE OF SUBMISSION');
        sections.push(isValueNotFound(criteria.submission_type) 
            ? 'Not found' 
            : formatValueForCopy(criteria.submission_type));
        sections.push('');
        
        // Proposal Requirements
        sections.push('PROPOSAL REQUIREMENTS');
        sections.push(isValueNotFound(criteria.proposal_requirements) 
            ? 'Not found' 
            : formatValueForCopy(criteria.proposal_requirements));
        sections.push('');
        
        // Award Criteria
        sections.push('AWARD / EVALUATION CRITERIA');
        sections.push(isValueNotFound(criteria.award_criteria) 
            ? 'Not found' 
            : formatValueForCopy(criteria.award_criteria));
        sections.push('');
        
        // Submission Method
        sections.push('SUBMISSION METHOD');
        sections.push(isValueNotFound(criteria.submission_method) 
            ? 'Not found' 
            : formatValueForCopy(criteria.submission_method));
        sections.push('');
        
        // Eligibility Criteria
        sections.push('ELIGIBILITY CRITERIA');
        sections.push(isValueNotFound(criteria.eligibility_criteria) 
            ? 'Not found' 
            : formatValueForCopy(criteria.eligibility_criteria));
        sections.push('');
        
        // Financial Parameters
        sections.push('FINANCIAL PARAMETERS');
        sections.push(isValueNotFound(criteria.financial_parameters) 
            ? 'Not found' 
            : formatValueForCopy(criteria.financial_parameters));
        
        return sections.join('\n');
    }, [criteria]);

    const handleCopyAll = () => {
        navigator.clipboard.writeText(formattedText).then(() => {
            onCopySuccess('All criteria copied to clipboard');
        }).catch(() => {
            onCopySuccess('Failed to copy to clipboard');
        });
    };

    return (
        <Box sx={{ mt: 3 }}>
            {/* Header with title and actions */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start', 
                mb: 2,
                gap: 2
            }}>
                <Typography 
                    variant="h5" 
                    sx={{ 
                        fontWeight: 600, 
                        color: 'primary.main',
                        flex: 1,
                        lineHeight: 1.3
                    }}
                >
                    {criteria.project_title || 'Extracted Criteria'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopyAll}
                    >
                        Copy All
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={onReset}
                    >
                        New ToR
                    </Button>
                </Box>
            </Box>

            {/* Single formatted output block */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    backgroundColor: 'rgba(34, 184, 167, 0.06)',
                    border: '1px solid rgba(34, 184, 167, 0.2)',
                    borderRadius: 2,
                    fontFamily: '"Inter", "Roboto", sans-serif'
                }}
            >
                <Box
                    sx={{
                        fontSize: '0.95rem',
                        lineHeight: 1.7,
                        color: 'text.primary',
                    }}
                >
                    {/* Project Title */}
                    {criteria.project_title && (
                        <>
                            <Typography 
                                component="span" 
                                sx={{ 
                                    display: 'block',
                                    fontWeight: 600,
                                    fontSize: '1.1rem',
                                    color: 'text.primary',
                                    mb: 2,
                                    pb: 1.5,
                                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                                }}
                            >
                                {criteria.project_title}
                            </Typography>
                        </>
                    )}

                    {/* Deadline Dates */}
                    <Typography 
                        component="div" 
                        sx={{ 
                            fontWeight: 700, 
                            color: 'primary.main', 
                            fontSize: '0.85rem', 
                            letterSpacing: '0.5px',
                            mb: 0.5
                        }}
                    >
                        DEADLINE DATES
                    </Typography>
                    <Box sx={{ mb: 2.5 }}>
                        {renderFormattedValue(criteria.deadline_dates)}
                        <SourceCitation source={criteria.deadline_source} />
                    </Box>

                    {/* Clarification Deadline */}
                    <Typography 
                        component="div" 
                        sx={{ 
                            fontWeight: 700, 
                            color: 'primary.main', 
                            fontSize: '0.85rem', 
                            letterSpacing: '0.5px',
                            mb: 0.5
                        }}
                    >
                        CLARIFICATION DEADLINE
                    </Typography>
                    <Box sx={{ mb: 2.5 }}>
                        {renderFormattedValue(criteria.clarification_date)}
                    </Box>

                    {/* Submission Type */}
                    <Typography 
                        component="div" 
                        sx={{ 
                            fontWeight: 700, 
                            color: 'primary.main', 
                            fontSize: '0.85rem', 
                            letterSpacing: '0.5px',
                            mb: 0.5
                        }}
                    >
                        TYPE OF SUBMISSION
                    </Typography>
                    <Box sx={{ mb: 2.5 }}>
                        {renderFormattedValue(criteria.submission_type)}
                    </Box>

                    {/* Submission Method */}
                    <Typography 
                        component="div" 
                        sx={{ 
                            fontWeight: 700, 
                            color: 'primary.main', 
                            fontSize: '0.85rem', 
                            letterSpacing: '0.5px',
                            mb: 0.5
                        }}
                    >
                        SUBMISSION METHOD
                    </Typography>
                    <Box sx={{ mb: 2.5 }}>
                        {renderFormattedValue(criteria.submission_method)}
                        <SourceCitation source={criteria.submission_source} />
                    </Box>

                    {/* Proposal Requirements */}
                    <Typography 
                        component="div" 
                        sx={{ 
                            fontWeight: 700, 
                            color: 'primary.main', 
                            fontSize: '0.85rem', 
                            letterSpacing: '0.5px',
                            mb: 0.5
                        }}
                    >
                        PROPOSAL REQUIREMENTS
                    </Typography>
                    <Box sx={{ mb: 2.5 }}>
                        {renderFormattedValue(criteria.proposal_requirements)}
                    </Box>

                    {/* Award / Evaluation Criteria */}
                    <Typography 
                        component="div" 
                        sx={{ 
                            fontWeight: 700, 
                            color: '#9c27b0', 
                            fontSize: '0.85rem', 
                            letterSpacing: '0.5px',
                            mb: 0.5
                        }}
                    >
                        AWARD / EVALUATION CRITERIA
                    </Typography>
                    <Box sx={{ mb: 2.5 }}>
                        {renderFormattedValue(criteria.award_criteria)}
                    </Box>

                    {/* Eligibility Criteria */}
                    <Typography 
                        component="div" 
                        sx={{ 
                            fontWeight: 700, 
                            color: 'primary.main', 
                            fontSize: '0.85rem', 
                            letterSpacing: '0.5px',
                            mb: 1
                        }}
                    >
                        ELIGIBILITY CRITERIA
                    </Typography>
                    <Box sx={{ mb: 2.5 }}>
                        {renderEligibilityCriteria(criteria.eligibility_criteria)}
                        <SourceCitation source={criteria.eligibility_source} />
                    </Box>

                    {/* Financial Parameters */}
                    <Typography 
                        component="div" 
                        sx={{ 
                            fontWeight: 700, 
                            color: '#4caf50',
                            fontSize: '0.85rem', 
                            letterSpacing: '0.5px',
                            mb: 0.5
                        }}
                    >
                        FINANCIAL PARAMETERS
                    </Typography>
                    <Box>
                        {renderFormattedValue(criteria.financial_parameters)}
                        <SourceCitation source={criteria.financial_source} />
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

const TorCriteriaPage: React.FC = () => {
    const [file, setFile] = useState<File | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [criteria, setCriteria] = useState<TorCriteria | null>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const extractTextFromPDF = async (pdfFile: File): Promise<string> => {
        try {
            const arrayBuffer = await pdfFile.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
            const textPages: string[] = [];

            for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                const page = await pdf.getPage(pageNum);
                const textContent = await page.getTextContent();

                let lastY = -1;
                let pageText = '';

                for (const item of textContent.items) {
                    const textItem = item as any;

                    if (lastY !== -1 && Math.abs(textItem.transform[5] - lastY) > 5) {
                        pageText += '\n';
                    }

                    pageText += textItem.str;
                    lastY = textItem.transform[5];
                }

                textPages.push(pageText);
            }

            return textPages.join('\n\n');
        } catch (err) {
            console.error('PDF extraction failed:', err);
            throw new Error('Failed to extract text from PDF. The PDF may be corrupted or image-based.');
        }
    };

    const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        if (selectedFile) {
            if (selectedFile.type !== 'application/pdf') {
                setError('Only PDF files are supported.');
                return;
            }
            const maxSize = 10 * 1024 * 1024; // 10MB
            if (selectedFile.size > maxSize) {
                setError('File size must be less than 10MB.');
                return;
            }
            setFile(selectedFile);
            setError(null);
            setCriteria(null);
        }
    }, []);

    const handleUpload = async () => {
        if (!file) {
            setError('Please select a file first.');
            return;
        }

        setIsProcessing(true);
        setError(null);

        try {
            // Extract text from PDF
            const torText = await extractTextFromPDF(file);

            if (!torText || torText.trim().length < 100) {
                throw new Error('PDF extraction failed or returned insufficient text. The PDF may be scanned/image-based. Try a text-based PDF or convert the scanned document using OCR software.');
            }

            // Send to API for LLM extraction
            const response = await axios.post<TorCriteria>(
                `${API_URL}/extract-tor-criteria`,
                { tor_text: torText },
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true,
                    timeout: 120000 // 2 minutes
                }
            );

            setCriteria(response.data);

        } catch (err: any) {
            console.error('ToR criteria extraction failed:', err);
            
            if (axios.isAxiosError(err)) {
                if (err.code === 'ERR_NETWORK' || err.code === 'ERR_CONNECTION_CLOSED') {
                    setError('Extraction service is not available. Please try again later.');
                } else if (err.response?.status === 401) {
                    setError('Authentication required. Please log in.');
                } else if (err.response?.status === 500) {
                    setError('Extraction service error. Please try again.');
                } else {
                    setError(err.response?.data?.error || 'Failed to extract criteria from ToR.');
                }
            } else {
                setError(err.message || 'Failed to extract criteria from ToR.');
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const handleReset = () => {
        setFile(null);
        setCriteria(null);
        setError(null);
    };

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
            <Typography variant="h4" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                ToR Criteria Extractor
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                Upload a Terms of Reference document to extract key submission criteria including deadlines, 
                clarification dates, submission methods, and eligibility requirements.
            </Typography>

            {/* Upload Section */}
            {!criteria && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 4,
                        textAlign: 'center',
                        border: '2px dashed',
                        borderColor: file ? 'primary.main' : 'divider',
                        borderRadius: 3,
                        backgroundColor: file ? 'rgba(34, 184, 167, 0.04)' : 'transparent',
                        transition: 'all 0.2s ease'
                    }}
                >
                    <input
                        type="file"
                        accept=".pdf"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        id="tor-file-input"
                        disabled={isProcessing}
                    />
                    <label htmlFor="tor-file-input">
                        <Button
                            variant="outlined"
                            component="span"
                            startIcon={<UploadFileIcon />}
                            disabled={isProcessing}
                            sx={{ mb: 2 }}
                        >
                            {file ? 'Change File' : 'Select ToR PDF'}
                        </Button>
                    </label>

                    {file && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                Selected: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={handleUpload}
                                disabled={isProcessing}
                                startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                                sx={{ minWidth: 200 }}
                            >
                                {isProcessing ? 'Extracting Criteria...' : 'Extract Criteria'}
                            </Button>
                        </Box>
                    )}

                    {!file && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                            Supported format: PDF (max 10MB)
                        </Typography>
                    )}
                </Paper>
            )}

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Results Section */}
            {criteria && (
                <CriteriaResults 
                    criteria={criteria} 
                    onReset={handleReset}
                    onCopySuccess={(msg) => {
                        setSnackbarMessage(msg);
                        setSnackbarOpen(true);
                    }}
                />
            )}

            {/* Snackbar for copy confirmation */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={2000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
};

export default TorCriteriaPage;
